// Single source of truth for "which result block is the primary one to render".
//
// Phase 12 introduced result.strategy — the TradingView-faithful NETTING account (one
// always-in-market position, stop-and-reverse, SL/TP/trailing, equity MTM). It is now the
// primary result; result.merged / .long / .short are kept only as single-direction
// diagnostics. Every report component prefers result.strategy via selectPrimary().
//
// Netting's field names deliberately match the SCAN shape (maxStratDD / buyHoldReturn /
// returns[] / equityByBar / barTimes / sharpe / sortino / grossProfit / …), so the
// existing `"maxDD" in primary ? … : …` narrowing in each component still resolves
// correctly for a netting object (it has maxStratDD, not maxDD).

import type {
  BacktestResponseDto,
  MergeResultDto,
  NettingExitType,
  NettingResultDto,
  ScanResultDto,
} from "../../api/types";

export type PrimaryResult = NettingResultDto | MergeResultDto | ScanResultDto;

/** Prefer the Phase-12 netting account; fall back to legacy merged, then single scans. */
export function selectPrimary(result: BacktestResponseDto): PrimaryResult | null {
  return result.strategy ?? result.merged ?? result.long ?? result.short ?? null;
}

/** Human label for the primary block (shown next to chart/report titles). */
export function primaryLabel(result: BacktestResponseDto): string {
  if (result.strategy) return "Netting";
  if (result.merged) return "Merged";
  if (result.long) return "Long";
  if (result.short) return "Short";
  return "—";
}

export function isNetting(result: BacktestResponseDto): boolean {
  return !!result.strategy;
}

// Visual language for the netting exit types — used by the chart markers, the trade list,
// and the diagnostics legend so the colour/label of an "sl" exit is identical everywhere.
export interface ExitMeta {
  /** Full label for tables/tooltips. */
  label: string;
  /** 1–2 char code for dense chart markers. */
  short: string;
  /** Marker / chip colour. */
  color: string;
}

const EXIT_META: Record<NettingExitType, ExitMeta> = {
  sl:        { label: "Stop loss",     short: "SL", color: "#f85149" }, // red
  tp:        { label: "Take profit",   short: "TP", color: "#3fb950" }, // green
  trailing:  { label: "Trailing stop", short: "TR", color: "#d29922" }, // amber
  reverse:   { label: "Reverse (SAR)", short: "R",  color: "#58a6ff" }, // blue (flip)
  bars:      { label: "Bars exit",     short: "B",  color: "#8b949e" }, // grey
  indicator: { label: "Indicator exit", short: "I", color: "#8b949e" },
  end:       { label: "Scan end",      short: "E",  color: "#6e7681" },
};

export function exitMeta(exitType: string): ExitMeta {
  return EXIT_META[exitType as NettingExitType] ?? { label: exitType, short: "?", color: "#8b949e" };
}
